import { MonetaryAmount } from '@components'
import type { JournalReportDocument } from '@query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { EntryPanelPagination } from './EntryPanelPagination'
import { JournalEntryShimmerRows } from './EntryPanelShimmer'
import {
  AccountCode,
  Caret,
  EntryHeaderRow,
  EntryPanel,
  ReportRow,
  RowLabel,
  RowMeta,
  ValueCell
} from './interactive.styled'

/** Narrow trailing slot for the unbalanced icon; keeps debit/credit columns aligned. */
const STATUS_ICON_WIDTH = '28px'

type Props = {
  doc: JournalReportDocument
  currency?: string
  isLoading?: boolean
  entriesPage: number
  entriesPageCount: number
  entriesTotalCount: number
  entriesPageSize?: number
  onEntriesPageChange: (page: number) => void
}

export const JournalDocumentEntries: React.FC<Props> = ({
  doc,
  currency,
  isLoading = false,
  entriesPage,
  entriesPageCount,
  entriesTotalCount,
  entriesPageSize,
  onEntriesPageChange
}) => {
  const [t] = useTranslation()
  const showPagination = entriesTotalCount > 0

  return (
    <EntryPanel>
      <EntryHeaderRow>
        <Caret />
        <AccountCode />
        <RowLabel $header>{t('reports.interactive.account')}</RowLabel>
        <ValueCell $width="120px" $header>
          {t('reports.interactive.debit')}
        </ValueCell>
        <ValueCell $width="120px" $header>
          {t('reports.interactive.credit')}
        </ValueCell>
        <ValueCell $width={STATUS_ICON_WIDTH} $header />
      </EntryHeaderRow>
      {isLoading ? (
        <JournalEntryShimmerRows statusIconWidth={STATUS_ICON_WIDTH} />
      ) : (
        doc.entries.map((entry, index) => (
          <ReportRow key={`${doc.number}-${index}`} $entryRow $bg="#f7fafd">
            <Caret />
            <AccountCode>{entry.account_number}</AccountCode>
            <RowLabel>
              {entry.account_name}
              {entry.description ? <RowMeta>{entry.description}</RowMeta> : null}
            </RowLabel>
            <ValueCell $width="120px">
              {entry.debet ? <MonetaryAmount value={entry.debet} currency={currency} /> : null}
            </ValueCell>
            <ValueCell $width="120px">
              {entry.credit ? <MonetaryAmount value={entry.credit} currency={currency} /> : null}
            </ValueCell>
            <ValueCell $width={STATUS_ICON_WIDTH} />
          </ReportRow>
        ))
      )}
      {showPagination && (
        <EntryPanelPagination
          page={entriesPage}
          pageCount={entriesPageCount}
          totalCount={entriesTotalCount}
          pageSize={entriesPageSize}
          onPageChange={onEntriesPageChange}
        />
      )}
    </EntryPanel>
  )
}
